import type {
  AnswerMovement,
  AreaSummary,
  ChildLearning,
  GameId,
  LearningArea,
  MovementSummary,
} from "../../../shared/api/answers";
import { AREA_BY_GAME } from "../../../shared/api/answers";
import { LEVEL_COUNT } from "../../../shared/curriculum/progress";
import { gameTitle } from "../../../shared/ui/gameTitles";

/**
 * The per-question learning summary (`GET /learning/players/:id/summary`) as a
 * parent reads it — on the Progress screen and on the printed report. Pure, so
 * every branch is tested without a DOM (OD-60).
 *
 * 🔴 FIVE AREAS, ALWAYS, IN CURRICULUM ORDER. The server promises all five
 * zero-filled; `fiveAreas` keeps that promise even when a reply does not, so an
 * area the child has not reached is a card saying so — never a missing card a
 * parent reads as "this school does not teach letters".
 *
 * 🔴 A LEVEL, NEVER A PERCENTAGE (ENGINEERING-GUIDE §8): the headline is
 * "Numbers · Level 3", and every count carries its denominator ("12 of 15").
 *
 * 🔴 THE USUAL TIME IS THE MEDIAN (`metricKeys.ts`). One long pause — a child
 * who walked off to get water — drags a mean a long way; the median is the time
 * half the answers beat. The mean is offered only as a secondary detail.
 *
 * ⚠️ `pose_missed` AND `no_answer` ARE NOT WRONG ANSWERS. A Turo pose not quite
 * copied and a Kalqy World question left unanswered are counted as questions
 * met, never as mistakes — `wrong` is the only "still learning" count here.
 *
 * ⚠️ MOVEMENT IS THE ACT THE DEVICE DECIDED (I1), as a category — "jumping",
 * "stepping". Nothing here is a body measurement, and nothing could be.
 */

export type { LearningArea };

/** Curriculum order — the order the child's home screen shows the games in. */
export const AREA_ORDER: readonly GameId[] = [
  "sky-quest",
  "letter-lagoon",
  "turos-world",
  "kalqy-world",
  "bridge-patterns",
];

/** The five learning areas, in the product owner's words. */
export const AREA_LABEL: Record<LearningArea, string> = {
  numbers: "Numbers",
  letters_words: "Letters & words",
  general_awareness: "General awareness",
  social_awareness: "Social awareness",
  matching_sorting: "Matching & sorting",
};

/** "Numbers" for a game id. */
export function areaLabel(game: GameId): string {
  return AREA_LABEL[AREA_BY_GAME[game]];
}

/* ── durations ───────────────────────────────────────────────────────────── */

/**
 * "about 4 s", "under 1 s", "about 1 min 5 s" — or null when there is no time.
 *
 * Whole seconds, so sibling times share one precision. "about" because the
 * server's median comes from a latency histogram (within ~20%), and a parent
 * should not read "4 s" as a stopwatch reading.
 */
export function durationLabel(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return null;
  if (ms < 1000) return "under 1 s";
  const s = Math.round(ms / 1000);
  if (s < 60) return `about ${String(s)} s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest === 0 ? `about ${String(m)} min` : `about ${String(m)} min ${String(rest)} s`;
}

/* ── zero-fill ───────────────────────────────────────────────────────────── */

const MOVEMENTS: readonly AnswerMovement[] = ["jump", "reach", "step", "lean", "point", "carry", "pose", "tap", "none"];

export function emptyMovement(): MovementSummary {
  return {
    byMovement: Object.fromEntries(MOVEMENTS.map((m) => [m, 0])) as Record<AnswerMovement, number>,
    itemsMoved: 0,
    itemsStill: 0,
    avgTtfMs: null,
    medianTtfMs: null,
    posesMet: 0,
    posesMissed: 0,
  };
}

export function emptyArea(game: GameId): AreaSummary {
  return {
    game,
    area: AREA_BY_GAME[game],
    questions: 0,
    answers: 0,
    correct: 0,
    firstTry: 0,
    wrong: 0,
    noAnswer: 0,
    avgTtrMs: null,
    medianTtrMs: null,
    levels: [],
    movement: emptyMovement(),
    lastPlayedAt: null,
  };
}

const count = (n: unknown): number => (typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

/** Exactly the five games, in `AREA_ORDER`; a missing one is zero-filled, an unknown one dropped, a repeat ignored. */
export function fiveAreas(areas: readonly AreaSummary[] | null | undefined): AreaSummary[] {
  const byGame = new Map<GameId, AreaSummary>();
  for (const a of areas ?? []) {
    if (AREA_ORDER.includes(a.game) && !byGame.has(a.game)) byGame.set(a.game, a);
  }
  return AREA_ORDER.map((game) => {
    const a = byGame.get(game);
    if (!a) return emptyArea(game);
    /* A partial movement block (an older server) still reads as zeros, not as undefined. */
    const blank = emptyMovement();
    const m: Partial<MovementSummary> = (a.movement as MovementSummary | undefined) ?? {};
    return {
      ...a,
      area: AREA_BY_GAME[game],
      levels: Array.isArray(a.levels) ? a.levels : [],
      movement: { ...blank, ...m, byMovement: { ...blank.byMovement, ...m.byMovement } },
    };
  });
}

/* ── one area ────────────────────────────────────────────────────────────── */

export type LevelStateWord = "finished" | "playing" | "not-yet";

export const LEVEL_WORD: Record<LevelStateWord, string> = {
  finished: "Finished",
  playing: "Playing",
  "not-yet": "Not yet",
};

export interface LevelStep {
  level: number;
  state: LevelStateWord;
  questions: number;
}

export interface AreaRow {
  game: GameId;
  label: string;
  title: string;
  /** "Numbers · Level 2", or just "Numbers" before any play. */
  headline: string;
  /** Highest level with a question in the period; null when none. */
  level: number | null;
  played: boolean;
  /** Every question met, minus the ones left unanswered. Poses count — they were done. */
  answered: number;
  /** Answers that were right or wrong (poses and unanswered are neither). */
  answers: number;
  firstTry: number;
  /** "12 of 15", or null when nothing was right-or-wrong. */
  firstTryLine: string | null;
  /** Wrong answers only — never a missed pose or an unanswered question. */
  stillLearning: number;
  posesMet: number;
  poses: number;
  /** "4 of 6", or null when the game asked no poses. */
  posesLine: string | null;
  usualTime: string | null;
  averageTime: string | null;
  levels: LevelStep[];
  /** Levels marked complete. */
  finished: number;
}

/** "9 of 12" — the denominator always travels with the count. */
export function ofLine(yes: number, n: number): string | null {
  return n <= 0 ? null : `${yes.toLocaleString("en-IN")} of ${n.toLocaleString("en-IN")}`;
}

export function areaRow(a: AreaSummary): AreaRow {
  const steps: LevelStep[] = [];
  for (let level = 1; level <= LEVEL_COUNT; level++) {
    const l = a.levels.find((x) => x.level === level);
    const questions = count(l?.questions);
    steps.push({ level, questions, state: l?.completed ? "finished" : questions > 0 ? "playing" : "not-yet" });
  }
  const playedLevels = steps.filter((s) => s.questions > 0).map((s) => s.level);
  const level = playedLevels.length > 0 ? Math.max(...playedLevels) : null;
  const label = areaLabel(a.game);
  const questions = count(a.questions);
  const answered = Math.max(0, questions - count(a.noAnswer));
  const answers = count(a.answers);
  const firstTry = Math.min(count(a.firstTry), answers);
  const posesMet = count(a.movement.posesMet);
  const poses = posesMet + count(a.movement.posesMissed);
  return {
    game: a.game,
    label,
    title: gameTitle(a.game, "en"),
    headline: level === null ? label : `${label} · Level ${String(level)}`,
    level,
    played: questions > 0,
    answered,
    answers,
    firstTry,
    firstTryLine: ofLine(firstTry, answers),
    stillLearning: count(a.wrong),
    posesMet,
    poses,
    posesLine: ofLine(posesMet, poses),
    usualTime: durationLabel(a.medianTtrMs),
    averageTime: durationLabel(a.avgTtrMs),
    levels: steps,
    finished: steps.filter((s) => s.state === "finished").length,
  };
}

/* ── movement ────────────────────────────────────────────────────────────── */

/** The movements that ARE movement. `tap` is the keyboard/touch twin and `none` a timeout. */
export const MOVEMENT_CATEGORIES = ["jump", "reach", "step", "lean", "point", "carry", "pose"] as const;
export type MovementCategory = (typeof MOVEMENT_CATEGORIES)[number];

export const MOVEMENT_WORD: Record<MovementCategory, { label: string; verb: string }> = {
  jump: { label: "Jumping", verb: "jumping" },
  reach: { label: "Reaching", verb: "reaching" },
  step: { label: "Stepping", verb: "stepping" },
  lean: { label: "Leaning", verb: "leaning" },
  point: { label: "Pointing", verb: "pointing" },
  carry: { label: "Carrying", verb: "carrying" },
  pose: { label: "Poses", verb: "copying poses" },
};

/** Every area's movement added up. The averages cannot be added, so they are left out (null). */
export function totalMovement(areas: readonly AreaSummary[]): MovementSummary {
  const total = emptyMovement();
  for (const a of areas) {
    const m = a.movement;
    for (const k of MOVEMENTS) total.byMovement[k] += count(m.byMovement[k]);
    total.itemsMoved += count(m.itemsMoved);
    total.itemsStill += count(m.itemsStill);
    total.posesMet += count(m.posesMet);
    total.posesMissed += count(m.posesMissed);
  }
  return total;
}

export interface MovementRow {
  movement: MovementCategory;
  label: string;
  count: number;
}

/** The movements used, most first (ties in category order); zero rows dropped. */
export function movementRows(m: MovementSummary): MovementRow[] {
  return MOVEMENT_CATEGORIES.map((movement) => ({ movement, label: MOVEMENT_WORD[movement].label, count: count(m.byMovement[movement]) }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || MOVEMENT_CATEGORIES.indexOf(a.movement) - MOVEMENT_CATEGORIES.indexOf(b.movement));
}

/**
 * "Answered mostly by jumping and stepping." in plain words, or null before
 * any answer. One movement past 60% of the moving answers is named alone; else
 * the top two.
 */
export function movementSentence(m: MovementSummary): string | null {
  const rows = movementRows(m);
  if (rows.length === 0) {
    return count(m.byMovement.tap) > 0 ? "Answered by tapping rather than by moving." : null;
  }
  const verb = (r: MovementRow) => MOVEMENT_WORD[r.movement].verb;
  if (rows.length === 1) return `Answered by ${verb(rows[0])}.`;
  const total = rows.reduce((s, r) => s + r.count, 0);
  if (rows[0].count >= total * 0.6) return `Answered mostly by ${verb(rows[0])}.`;
  return `Answered mostly by ${verb(rows[0])} and ${verb(rows[1])}.`;
}

/** "Moved for 18 of 20 questions, stood still for 2" — null before any question. */
export function movedLine(m: MovementSummary): string | null {
  const moved = count(m.itemsMoved);
  const still = count(m.itemsStill);
  const n = moved + still;
  if (n === 0) return null;
  const line = `Moved for ${String(moved)} of ${String(n)} ${n === 1 ? "question" : "questions"}`;
  return still > 0 ? `${line}, stood still for ${String(still)}` : line;
}

/** Each played game's usual time to first movement (median), with the mean beside it. Games with no time are left out. */
export function firstMoveRows(areas: readonly AreaSummary[]): { game: GameId; title: string; usual: string; average: string | null }[] {
  const out: { game: GameId; title: string; usual: string; average: string | null }[] = [];
  for (const a of areas) {
    const usual = durationLabel(a.movement.medianTtfMs);
    if (usual === null) continue;
    out.push({ game: a.game, title: gameTitle(a.game, "en"), usual, average: durationLabel(a.movement.avgTtfMs) });
  }
  return out;
}

/* ── the printed report ──────────────────────────────────────────────────── */

export interface ReportLearning {
  areas: AreaRow[];
  /** Questions answered across the five. */
  answered: number;
  movement: {
    sentence: string | null;
    moved: string | null;
    rows: MovementRow[];
  };
}

/**
 * What the printed sheet shows of the learning summary: the five areas and how
 * the child answered. Null when the read did not arrive — the sheet prints what
 * it has and says nothing about what it does not.
 *
 * ⚠️ COUNTS ONLY. The sheet's footer promises "every number here is a count of
 * real activity — there are no estimates", and a median time from a histogram
 * is an estimate, so times stay on the screen, where they say "about".
 */
export function reportLearning(learning: ChildLearning | null | undefined): ReportLearning | null {
  if (!learning) return null;
  const areas = fiveAreas(learning.areas);
  const total = totalMovement(areas);
  const rows = areas.map(areaRow);
  return {
    areas: rows,
    answered: rows.reduce((s, r) => s + r.answered, 0),
    movement: { sentence: movementSentence(total), moved: movedLine(total), rows: movementRows(total) },
  };
}
